import React, { useState, useContext, useRef, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import TermsConditionsModal from '../about/TermsConditionsModal';
import PrivacyPolicyModal from '../about/PrivacyPolicyModal';
import LoginModal from './LoginModal';
import { AppContext } from '../window';
import { AuthorizedUser } from '../models/User';
import Loader from '../libs/Loader';
import { NotificationContext } from '../context/NotificationContext';
import RecaptchaV2, { RecaptchaV2Handle } from '../libs/RecaptchaV2';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WcIcon from '@mui/icons-material/Wc';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { logDataLayerEvent } from '../utils/tagManager';
import { getEmailAndNicknameFromQuery } from '../utils/queryPrefill';
import { useTranslation, Trans } from '../i18n';

interface SignupMultistepProps {
  onSignupSuccess?: () => void;
  initialMessage?: string;
  className?: string;
  initialGender?: string;
  initialAgeRange?: string;
  initialTags?: number[];
  initialLikesAttributes?: number[];
  submitButtonText?: string;
}

const SignupMultistep: React.FC<SignupMultistepProps> = ({ 
  onSignupSuccess, 
  initialMessage, 
  className,
  initialGender,
  initialAgeRange,
  initialTags,
  initialLikesAttributes,
  submitButtonText
}) => {
  const { t } = useTranslation();
  const defaultSubmitButtonText = submitButtonText || t('auth.create_account');
  // Calculate default age from age range
  const getDefaultAge = (ageRange?: string) => {
    if (!ageRange) return { day: '', month: '', year: '' };
    
    const currentYear = new Date().getFullYear();
    let age = 25; // default
    
    switch(ageRange) {
      case '18-24': age = 21; break;
      case '25-34': age = 29; break;
      case '35-44': age = 39; break;
      case '45+': age = 48; break;
    }
    
    const birthYear = currentYear - age;
    return { day: '15', month: '6', year: birthYear.toString() };
  };

  const prefilledValues = useMemo(() => getEmailAndNicknameFromQuery(), []);
  const defaultAge = getDefaultAge(initialAgeRange);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    email: prefilledValues.email,
    password: '',
    nickname: prefilledValues.nickname,
    day: defaultAge.day,
    month: defaultAge.month,
    year: defaultAge.year,
    gender: initialGender || 'male',
    agreeToTerms: false
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const appContext = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const recaptchaRef = useRef<RecaptchaV2Handle>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const totalSteps = 4;
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (step: number): string | null => {
    switch (step) {
      case 1:
        if (!formData.nickname) {
          return t('auth.validation_nickname_required');
        }
        if (formData.nickname.length < 2) {
          return t('auth.validation_nickname_length');
        }
        break;
      case 2:
        if (!formData.day || !formData.month || !formData.year) {
          return t('auth.validation_birthdate_complete');
        }
        break;
      case 3:
        if (!formData.gender) {
          return t('auth.validation_gender');
        }
        break;
      case 4:
        if (!formData.email) {
          return t('auth.validation_email_required');
        }
        if (!formData.email.includes('@')) {
          return t('auth.validation_valid_email');
        }
        if (!formData.password) {
          return t('auth.validation_password_required');
        }
        if (formData.password.length < 6) {
          return t('auth.validation_password_length');
        }
        if (!formData.agreeToTerms) {
          return t('auth.validation_agree_terms');
        }
        break;
    }
    return null;
  };

  const handleNextStep = () => {
    setErrorMessage(null);
    const validationError = validateStep(currentStep);
    
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    setErrorMessage(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRecaptchaVerify = async (token: string) => {
    setRecaptchaToken(token);
    if (token) {
      submitFormWithToken(formDataRef.current, token);
    }
  };

  const handleRecaptchaExpire = () => {
    setRecaptchaToken(null);
  };

  const submitFormWithToken = async (data: typeof formData, token?: string) => {
    setIsSubmitting(true);

    try {
      const submitData = new URLSearchParams();
      submitData.append('UserCredentials[email]', data.email);
      submitData.append('UserCredentials[password]', data.password);
      submitData.append('User[nickname]', data.nickname);
      submitData.append('User[day]', data.day);
      submitData.append('User[month]', data.month);
      submitData.append('User[year]', data.year);
      submitData.append('User[gender]', data.gender);
      submitData.append('User[tcAgree]', data.agreeToTerms ? '1' : '0');
      
      // Add initial tags if provided
      if (initialTags && initialTags.length > 0) {
        initialTags.forEach((tagId) => {
          submitData.append('User[_tags][]', String(tagId));
        });
      }
      
      // Add initial likes attributes if provided
      if (initialLikesAttributes && initialLikesAttributes.length > 0) {
        initialLikesAttributes.forEach((attrId) => {
          submitData.append('User[_likesAttributes][]', String(attrId));
        });
      }
      
      if (token || recaptchaToken) {
        submitData.append('g-recaptcha-response', token || recaptchaToken || '');
      }

      const response = await fetch('/user/signup/do?json=1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });
      
      const responseData = await response.json();
      
      if (responseData.success) {
        setFormData({
          email: '',
          password: '',
          nickname: '',
          day: '',
          month: '',
          year: '',
          gender: 'male',
          agreeToTerms: false
        });
        
        const responseUser = responseData.user as AuthorizedUser | undefined;
        if (responseUser) {
          appContext.setUser(responseUser);
        }

        logDataLayerEvent('signup', {}, responseUser ?? appContext.user);
        
        addNotification({
          level: 'success',
          message: t('auth.signup_success'),
        });
        
        if (onSignupSuccess) {
          onSignupSuccess();
        }
      } else {
        setErrorMessage(responseData.error || t('auth.registration_failed'));
      }
    } catch (error) {
      setErrorMessage(t('auth.error_occurred'));
      console.error('Signup error:', error);
    } finally {
      setIsSubmitting(false);
      if (recaptchaRef.current && recaptchaRef.current.reset) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    
    const validationError = validateStep(currentStep);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (appContext.recaptchaSiteKey && !recaptchaToken) {
      if (recaptchaRef.current && recaptchaRef.current.execute) {
        recaptchaRef.current.execute();
      }
      return;
    }
    
    submitFormWithToken(formData, recaptchaToken || undefined);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3 className="text-center mb-4">{t('auth.step_nickname_title')}</h3>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <PersonIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Control 
                  type="text" 
                  name="nickname"
                  placeholder={t('auth.enter_nickname')}
                  value={formData.nickname}
                  onChange={handleInputChange}
                  required
                />
                <Form.Label>{t('auth.nickname_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content">
            <h3 className="text-center mb-4">{t('auth.step_birthdate_title')}</h3>
            <InputGroup className="mb-3 date-selector">
              <InputGroup.Text>
                <CalendarMonthIcon />
              </InputGroup.Text>
              <Form.Select 
                name="day" 
                value={formData.day} 
                onChange={handleInputChange}
                required
              >
                <option value="">{t('auth.day')}</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Form.Select>
              <Form.Select 
                name="month" 
                value={formData.month} 
                onChange={handleInputChange}
                required
              >
                <option value="">{t('auth.month')}</option>
                <option value="1">{t('auth.january')}</option>
                <option value="2">{t('auth.february')}</option>
                <option value="3">{t('auth.march')}</option>
                <option value="4">{t('auth.april')}</option>
                <option value="5">{t('auth.may')}</option>
                <option value="6">{t('auth.june')}</option>
                <option value="7">{t('auth.july')}</option>
                <option value="8">{t('auth.august')}</option>
                <option value="9">{t('auth.september')}</option>
                <option value="10">{t('auth.october')}</option>
                <option value="11">{t('auth.november')}</option>
                <option value="12">{t('auth.december')}</option>
              </Form.Select>
              <Form.Select 
                name="year" 
                value={formData.year} 
                onChange={handleInputChange}
                required
              >
                <option value="">{t('auth.year')}</option>
                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </InputGroup>
          </div>
        );
        
      case 3:
        return (
          <div className="step-content">
            <h3 className="text-center mb-4">{t('auth.step_gender_title')}</h3>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <WcIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Select 
                  name="gender" 
                  value={formData.gender} 
                  onChange={handleInputChange} 
                  required
                >
                  <option value="">{t('auth.select_gender')}</option>
                  {appContext.data.attributes.filter(attr => attr.name === 'gender').map((attr) => (
                    <option key={attr.id} value={attr.value.toLowerCase()}>{attr.translatedValue}</option>
                  ))}
                </Form.Select>
                <Form.Label>{t('auth.gender_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
          </div>
        );
        
      case 4:
        return (
          <div className="step-content">
            <h3 className="text-center mb-4">{t('auth.step_final_title')}</h3>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <EmailIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Control 
                  type="email" 
                  name="email"
                  placeholder={t('auth.enter_email')}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <Form.Label>{t('auth.email_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
            
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <LockIcon />
              </InputGroup.Text>
              <Form.Floating>
                <Form.Control 
                  type="password" 
                  name="password"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <Form.Label>{t('auth.password_required')}</Form.Label>
              </Form.Floating>
            </InputGroup>
            
            <Form.Check 
              type="checkbox"
              id="agreeToTerms"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleInputChange}
              label={
                <span style={{ fontSize: '0.875rem' }}>
                  <Trans
                    i18nKey="auth.tos_agree"
                    components={{
                      termsLink: (
                        <a
                          href="#"
                          style={{ fontSize: '0.875rem' }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTerms(true);
                          }}
                        />
                      ),
                      privacyLink: (
                        <a
                          href="#"
                          style={{ fontSize: '0.875rem' }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPrivacy(true);
                          }}
                        />
                      ),
                    }}
                  />
                </span>
              }
              className="mb-3 form-check-lg"
              required
            />
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className={`signup-multistep ${className || ''}`}>
      {initialMessage && (
        <Alert variant="info" className="mb-3">
          <small>{t('auth.signup_message_alert', { initialMessage })}</small>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger">{errorMessage}</Alert>
      )}

      {isSubmitting && (
        <Loader />
      )}

      <Form onSubmit={handleSubmit} className={isSubmitting ? 'd-none' : ''}>
        <div className="step-indicator">
          <div className="progress mb-4">
            <div 
              className="progress-bar bg-success" 
              role="progressbar" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              aria-valuenow={currentStep} 
              aria-valuemin={1} 
              aria-valuemax={totalSteps}
            />
          </div>
          <p className="text-center text-muted small">{t('auth.step_of', { current: currentStep, total: totalSteps })}</p>
        </div>

        {renderStepContent()}

        {appContext.recaptchaSiteKey && currentStep === totalSteps && (
          <RecaptchaV2
            ref={recaptchaRef}
            siteKey={appContext.recaptchaSiteKey}
            onVerify={handleRecaptchaVerify}
            onExpire={handleRecaptchaExpire}
            invisible={true}
          />
        )}

        <div className="d-flex justify-content-between mt-4">
          {currentStep > 1 && (
            <Button 
              variant="outline-secondary" 
              onClick={handlePreviousStep}
              disabled={isSubmitting}
            >
              <ArrowBackIcon sx={{ fontSize: 18, marginRight: 1 }} />
              {t('auth.back')}
            </Button>
          )}
          
          <div className={currentStep === 1 ? 'ms-auto' : ''}>
            {currentStep < totalSteps ? (
              <Button 
                variant="success" 
                onClick={handleNextStep}
                disabled={isSubmitting}
              >
                {t('auth.next')}
                <ArrowForwardIcon sx={{ fontSize: 18, marginLeft: 1 }} />
              </Button>
            ) : (
              <Button 
                variant="success" 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? t('auth.creating_account') : defaultSubmitButtonText}
              </Button>
            )}
          </div>
        </div>
      </Form>

      <div className="text-center mt-3">
        {t('auth.already_have_account')}{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setShowLogin(true);
          }}
        >
          {t('auth.login_here')}
        </a>
      </div>
      
      <TermsConditionsModal
        show={showTerms}
        onHide={() => setShowTerms(false)}
      />
      
      <PrivacyPolicyModal
        show={showPrivacy}
        onHide={() => setShowPrivacy(false)}
      />
      
      <LoginModal
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLoginSuccess={onSignupSuccess}
      />
    </div>
  );
};

export default SignupMultistep;